/**
 * Project switcher dropdown in the sidebar.
 *
 * Lists every project under the active workspace, highlights the selected one,
 * and "+ Create new project" opens an inline modal. Selection is persisted to
 * the workspace store + localStorage, and read by the Connections / Drafts /
 * Brand / Media / Templates / Automation / Sources pages instead of
 * `projects[0]`.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useMutation } from 'urql';
import { Q_PROJECTS, M_CREATE_PROJECT } from '@/lib/queries';
import { useWorkspace } from '@/stores/workspace.store';
import { useUi } from '@/stores/ui.store';

interface Props { collapsed: boolean }

const PROJECT_COLORS = ['bg-violet-500', 'bg-amber-500', 'bg-emerald-500', 'bg-pink-500', 'bg-blue-500', 'bg-stone-700'];

export function ProjectSwitcher({ collapsed }: Props) {
  const { toast } = useUi();
  const { project_id, setProject } = useWorkspace();
  const [{ data, fetching }, refetch] = useQuery({ query: Q_PROJECTS });
  const [, createProject] = useMutation(M_CREATE_PROJECT);

  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside to close
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const projects: any[] = data?.projects ?? [];
  // Self-heal: if no project_id in store yet (first render after sign-up) but the
  // user has at least one project, adopt the first one so downstream pages work.
  useEffect(() => {
    if (!project_id && projects.length > 0) {
      setProject(projects[0].id, projects[0].name);
    }
  }, [project_id, projects.length]);

  const current = projects.find((p) => p.id === project_id) ?? projects[0];

  const choose = (id: string, name: string) => {
    if (id === project_id) { setOpen(false); return; }
    setProject(id, name);
    setOpen(false);
    // Force a refetch of any project-scoped query so the new project's data loads.
    // No reload — components react to the store change.
    refetch({ requestPolicy: 'network-only' });
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const res = await createProject({ input: { name } });
      if (res.error) {
        toast(res.error.message ?? 'Create failed', 'error');
        return;
      }
      const created = res.data?.createProject;
      if (created) {
        setProject(created.id, created.name);
        toast(`Project "${name}" created`, 'success');
        setNewName('');
        setShowCreate(false);
        setOpen(false);
        refetch({ requestPolicy: 'network-only' });
      }
    } finally { setBusy(false); }
  };

  if (fetching && projects.length === 0) {
    return collapsed
      ? <span className="block w-8 h-8 rounded-lg bg-stone-200 animate-pulse" />
      : <span className="text-xs text-stone-400 px-2 block py-1.5">Loading projects…</span>;
  }

  if (!current && projects.length === 0) {
    // No projects yet — surface the Create button immediately
    return (
      <>
        <button onClick={() => setShowCreate(true)} type="button"
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-violet-700 hover:bg-violet-50 ${collapsed ? 'justify-center' : ''}`}>
          <span className="w-7 h-7 rounded-md bg-violet-100 text-violet-700 flex items-center justify-center text-lg font-bold flex-shrink-0">+</span>
          {!collapsed && <span className="text-sm font-medium flex-1 text-left">Create project</span>}
        </button>
        {showCreate && <CreateModal name={newName} setName={setNewName} busy={busy} onSubmit={handleCreate} onClose={() => setShowCreate(false)} />}
      </>
    );
  }

  const initial = current?.name?.[0]?.toUpperCase() ?? 'P';
  const idx = projects.indexOf(current);
  const color = PROJECT_COLORS[idx % PROJECT_COLORS.length];

  return (
    <>
      <div ref={ref} className="relative">
        <button onClick={() => setOpen((o) => !o)} type="button"
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-stone-100 ${collapsed ? 'justify-center' : ''}`}>
          <span className={`w-7 h-7 rounded-md ${color} text-white flex items-center justify-center text-sm font-bold flex-shrink-0`}>{initial}</span>
          {!collapsed && (
            <>
              <span className="flex-1 text-left min-w-0">
                <span className="block text-sm font-semibold text-stone-900 truncate">{current?.name}</span>
                <span className="block text-[10px] text-stone-500">Project</span>
              </span>
              {projects.length > 1 && <span className="text-stone-400 text-xs">▾</span>}
            </>
          )}
        </button>

        {open && (
          <div className={`absolute z-50 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden ${collapsed ? 'left-12 w-60' : 'left-0 right-0'}`}>
            <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Projects</p>
            {projects.map((p, i) => {
              const on = p.id === current?.id;
              const c = PROJECT_COLORS[i % PROJECT_COLORS.length];
              return (
                <button key={p.id} onClick={() => choose(p.id, p.name)} type="button"
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-stone-50 ${on ? 'bg-violet-50' : ''}`}>
                  <span className={`w-6 h-6 rounded ${c} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>{p.name?.[0]?.toUpperCase() ?? 'P'}</span>
                  <span className="flex-1 min-w-0 block text-sm font-medium text-stone-900 truncate">{p.name}</span>
                  {on && <span className="text-violet-600">✓</span>}
                </button>
              );
            })}
            <button
              onClick={() => { setOpen(false); setShowCreate(true); }}
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-violet-700 hover:bg-violet-50 border-t border-stone-100"
            >
              <span className="w-6 h-6 rounded bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold flex-shrink-0">+</span>
              <span className="text-sm font-medium">Create new project</span>
            </button>
          </div>
        )}
      </div>
      {showCreate && <CreateModal name={newName} setName={setNewName} busy={busy} onSubmit={handleCreate} onClose={() => setShowCreate(false)} />}
    </>
  );
}

function CreateModal({ name, setName, busy, onSubmit, onClose }: {
  name: string;
  setName: (n: string) => void;
  busy: boolean;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/40 px-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-stone-900 mb-1">Create a new project</h2>
        <p className="text-sm text-stone-500 mb-4">A project groups one brand's connections, drafts, and content.</p>
        <label className="block text-xs font-medium text-stone-600 mb-1">Project name</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Corp"
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
        <div className="flex items-center justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 rounded-lg">Cancel</button>
          <button type="submit" disabled={busy || !name.trim()}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-lg text-sm font-medium">
            {busy ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </form>
    </div>
  );
}
